import {assert} from 'Asserts';
import {Sequence} from 'components/Sequence';
import {convertMolBioStrandToGQLStrand} from 'constants/MolbioConstants';
import MetadataTabState from 'metadata/MetadataTabState';
import SequencePrimer, {PRIMER_PANEL_PRIMER_CHECKED} from 'molbio/primers/backbone/SequencePrimer';
import * as sequtil from 'molbio/SeqUtil';
import {getFieldEligibleOligoNucleotideTypes} from 'tag-schema/util/TagSchemaUtil';
import * as logging from 'util/Logging';

import {addPrimersToField} from './PrimerActions';
import {PrimerRowProps} from './PrimerRow';
import {PrimerTableProps} from './PrimerTable';
import {OligoSchemaFields} from './SelectedOligoActionsRow';

/**
 * Get all the oligo fields on the sequence's schema if one exists.
 */
export function getOligoFieldProps(sequence: Sequence): OligoSchemaFields {
  const schema = sequence.tagSchema();
  return schema
    ? schema.fields
        .map((field) => ({
          id: field.apiId,
          name: field.name,
          eligibleNucleotideTypes: getFieldEligibleOligoNucleotideTypes(field),
        }))
        .filter((oligoSchemaField) => oligoSchemaField.eligibleNucleotideTypes.size !== 0)
    : [];
}

export function getPropsForPrimer(primer: SequencePrimer, sequence: Sequence): PrimerRowProps {
  assert(Boolean(primer.get('_primer').get('primer_id')), 'Cannot display primers without underlying oligo');

  const position = sequtil.modelToDisplay(
    primer.bindPosition(sequence.get('_sequence')),
    sequence.startIndex()
  );

  return {
    name: primer.name(),
    active: primer.active(),
    selected: primer.selected(),
    strand: convertMolBioStrandToGQLStrand(primer.strand()),
    partnerSelected: primer.partnerSelected(),
    meltingTemp: primer.displayMeltingTemperature(),
    nucleotideType: primer.nucleotideType(),
    position,
    onPrimerRowClick: (shiftKeySelected) => {
      primer.click(sequence, shiftKeySelected);
    },
    checked: Boolean(primer.get(PRIMER_PANEL_PRIMER_CHECKED)),
    onPrimerToggleCheck: (checked) => {
      primer.set(PRIMER_PANEL_PRIMER_CHECKED, checked);
    },
    oligoId: primer.get('_primer').get('primer_api_id')!,
    bases: primer.bases(),
    displayLabel: primer.displayLabel(),
  };
}

/**
 * Given primers to show in a PrimerTable, extract props.
 */
export function getPrimerTableProps(sequence: Sequence): PrimerTableProps {
  const primers = sequence.sequencePrimers().models;
  const primersByCid: {[key: string]: SequencePrimer} = {};
  for (const primer of primers) {
    primersByCid[primer.cid] = primer;
  }
  return {
    toggleAllChecked: (checked: boolean) => {
      let hadStalePrimers = false;
      for (const primer of primers) {
        if (!primer) {
          hadStalePrimers = true;
          continue;
        }
        primer.set(PRIMER_PANEL_PRIMER_CHECKED, checked);
      }
      // TODO: The likely fix for this bug is to refactor these functions to reference the current primer
      // collection as an argument, and then make the closure inside the PrimerTable component, rather than
      // make a closure with the potentially stale collection here.
      // If there are stale primers, we have a bug. Log so we know how often this happens.
      if (hadStalePrimers) {
        logging.info('Primers were stale');
      }
    },
    onPrimerToggleCheck: (primerCid: string, checked: boolean) => {
      const primer = primersByCid[primerCid];
      if (!primer) {
        logging.info('Primers were stale');
        return;
      }
      primer.set(PRIMER_PANEL_PRIMER_CHECKED, checked);
    },
    onPrimerRowClick: (primerCid: string, shiftKeySelected: boolean) => {
      const primer = primersByCid[primerCid];
      if (!primer) {
        logging.info('Primers were stale');
        return;
      }
      primer.click(sequence, shiftKeySelected);
    },
    sequenceName: sequence.name(),
    sequenceId: sequence.get('id'),
    oligoSchemaFields: getOligoFieldProps(sequence),
    addPrimersToField: async (fieldId: string, oligoIds: Array<string>) => {
      const tags = await addPrimersToField(sequence.get('_sequence').get('id'), {
        schemaFieldId: fieldId,
        oligoIds,
      });
      if (tags) {
        sequence.setTags(tags);
        sequence.fileState().activateTab(MetadataTabState);
      }
    },
  };
}
